import type {
	AbstractSearchFormProps,
	AbstractSearchFormState,
} from "@component/assets/AbstractListingCalculator/AbstractSearchForm";
import { AbstractSearchForm } from "@component/assets/AbstractListingCalculator/AbstractSearchForm";
import { Config } from "@config";
import { 
	faMoneyBill1 as farMoneyBill1,
	faCreditCard as farCreditCard,
	faEnvelope as farEnvelope
 } from "@awesome.me/kit-3777c2145e/icons/classic/regular";
import { 
	faCalendarDays as fasCalendarDays,
	faSuitcase as fasSuitcase,
	faBuildingColumns as fasBuildingColumns,
	faLock as fasLock,
	faEarthAmericas as fasEarthAmericas,
	faCommentDollar as fasCommentDollar,
	faPhone as fasPhone,
	faInfinity as fasInfinity,
	faCommentSms as fasCommentSms
 } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatText } from "@ui/format";
import { logError } from "@utils/sentry-error";
import autobind from "autobind-decorator";
import { http, Alert, CollapseBlock, FormCheck, FormCurrency, FormDate, FormNumber, FormSelect } from "bm-ui-kit";
import type { CommonOptions } from "bm-ui-kit";
import React from "react";
import type { ReactNode } from "react";
import { Badge, Col, Row } from "react-bootstrap";
import { CountAmountField } from "./Components/CountAmountField";
import type { CreditCardCategory, RetailAccountSearchParams } from "./types";

type UserType = "worker" | "student" | "pensioner" | "publicServant" | "other";

const USER_TYPES: CommonOptions<UserType> = [
	{ label: "Alkalmazott", value: "worker" },
	{ label: "Diák", value: "student" },
	{ label: "Nyugdíjas", value: "pensioner" },
	{ label: "Közalkalmazott", value: "publicServant" },
	{ label: "Egyéb", value: "other" },
];

const CREDIT_CARD_CATEGORY: CommonOptions<CreditCardCategory> = [
	{ label: "Elektronikus", value: "ELECTRONIC" },
	{ label: "Dombornyomott", value: "EMBOSSED" },
	{ label: "Nem kérek bankkártyát", value: "NO_CARD" },
	{ label: "Mindegy", value: "NO_PREFERED" },
];

const MAX_RECEIPT_OF_MONEY_AMOUNT = 2_000_000_000;
const MAX_AGE = 150;
const MAX_AVG_VALUES = 200_000_000_000;
const MAX_LOAN_AMOUNT = 1_000_000_000;

type CollapseBlocks = "monthlyTransfer" | "cardAndCash" | "notification" | "admin";

interface State extends AbstractSearchFormState {
	banks: CommonOptions;
	collapse: Record<CollapseBlocks, boolean>;
}

interface Props extends AbstractSearchFormProps<RetailAccountSearchParams> {
	withOnePerBank?: boolean;
}

export class RetailAccountSearchForm extends AbstractSearchForm<RetailAccountSearchParams, Props, State> {
	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			banks: [],
			collapse: {
				monthlyTransfer: false,
				cardAndCash: false,
				notification: false,
				admin: false,
			},
		};
	}

	public componentDidMount(): void {
		super.componentDidMount();

		this.getBanks();
	}

	private getBanks(): void {
		this.promisePool
			.add(http.getWithCache(Config.getApiUrl("/api/public/bank/RETAIL_ACCOUNT")))
			.then((response) => {
				this.setState({
					banks: response.map((item) => ({ label: item.name, value: item.code })),
				});
			})
			.catch((error: Error) => {
				// TODO: Hibajelzés
				logError(error);
			});
	}

	public renderFields(values: RetailAccountSearchParams): ReactNode {
		const { withOnePerBank, highlightedBank } = this.props;

		return (
			<>
				{highlightedBank === "sberbank" && (
					<Alert variant="warning" icon="auto">
						A termék jelenleg nem elérhető, igénylésed az alábbi oldalunkon tudod leadni:{" "}
						<a href="/bankszamla/">Bankszámla kalkulátor</a>
					</Alert>
				)}
				<Row>
					<Col lg={6}>
						<div className="form-label required mb-1">Havonta a számlára érkező összeg</div>
						<Row>
							<Col sm={6}>
								<FormCurrency
									name="receiptOfMoneyAmount"
									validate={this.validReceiptOfMoneyAmount}
									required
								/>
							</Col>
							<Col sm={6}>
								<FormNumber
									name="piecesCount"
									append="részletben"
									min={1}
									max={50}
									validate={this.validPiecesCount}
									required
								/>
							</Col>
						</Row>
						<Row>
							<Col sm={6}>
								<FormCheck className="mb-1" label="Munkabér jellegű" name="salary" />
							</Col>
							<Col sm={6}>
								<FormCheck label="Nyugdíj jellegű" name="pension" />
							</Col>
						</Row>
					</Col>
					<Col lg={3} sm={6}>
						<FormNumber
							label="Életkor"
							name="age"
							min={0}
							max={150}
							prepend={<FontAwesomeIcon icon={fasCalendarDays} />}
							validate={this.validAge}
							required
						/>
					</Col>
					<Col lg={3} sm={6}>
						<FormSelect
							label="Jogviszony"
							name="userType"
							placeholder="Válassz ki egy lehetőséget!"
							options={USER_TYPES}
							prepend={<FontAwesomeIcon icon={fasSuitcase} />}
							required
						/>
					</Col>
					<Col lg={4} sm={6}>
						<FormSelect
							label="Jelenlegi számlavezető bankom"
							name="bank"
							placeholder="Válassz ki egy lehetőséget!"
							options={[{ label: "Jelenleg nincsen bankszámlám", value: "none" }, ...this.state.banks]}
							prepend={<FontAwesomeIcon icon={fasBuildingColumns} />}
						/>
					</Col>
					{withOnePerBank && (
						<Col xs={12}>
							<FormCheck label="Bankonként egy ajánlat" name="onePerBank" />
						</Col>
					)}
				</Row>
				<div className="pb-3">
					{this.renderMonthlyTransfer()}
					{this.renderCardAndCash()}
					{this.renderNotification()}
					{this.renderAdmin(values)}
				</div>
			</>
		);
	}

	private renderAdmin(values: RetailAccountSearchParams): ReactNode {
		if (!Config.getCurrentUser().hasRole("RETAIL_ACCOUNT")) {
			return undefined;
		}

		const collapseName = "admin";

		return (
			<CollapseBlock
				title={
					<>
						<Badge variant="warning" className="mr-2">
							<FontAwesomeIcon icon={fasLock} aria-label="Csak administrátorok számára" />
						</Badge>
						Extra beállítások
					</>
				}
				name={collapseName}
				active={this.state.collapse[collapseName]}
				onClick={this.onCollapseClick}
			>
				<Row>
					<Col lg={4} sm={6}>
						<FormSelect
							className="admin-only"
							label={
								<>
									<FontAwesomeIcon icon={fasLock} />
									Bankkártya típusa
								</>
							}
							name="creditCardCategory"
							placeholder="Válassz ki egy lehetőséget!"
							options={CREDIT_CARD_CATEGORY}
							prepend={<FontAwesomeIcon icon={fasBuildingColumns} />}
						/>
					</Col>
					<Col lg={4} sm={6}>
						<FormCurrency
							className="admin-only"
							label={
								<>
									<FontAwesomeIcon icon={fasLock} />
									Átlagos számlaegyenleg
								</>
							}
							name="averageAccountBalance"
							validate={this.validAverageAccountBalance}
						/>
					</Col>
					<Col lg={4} sm={6}>
						<FormCurrency
							className="admin-only"
							label={
								<>
									<FontAwesomeIcon icon={fasLock} />
									Átlagos megtakarítási állomány
								</>
							}
							name="averageSavings"
							validate={this.validAverageSavings}
						/>
					</Col>
					<Col lg={4} sm={6}>
						<FormDate
							className="admin-only"
							label={
								<>
									<FontAwesomeIcon icon={fasLock} />
									Kalkulálás időpontja
								</>
							}
							name="calculationDate"
							autoReset
							validate={this.validCalculationDate}
						/>
					</Col>
					<Col lg={4} sm={6}>
						<FormSelect
							className="admin-only"
							label={
								<>
									<FontAwesomeIcon icon={fasLock} />
									Hitelező bank
								</>
							}
							name="loanBank"
							placeholder="Válassz egy lehetőséget!"
							options={[{ label: "Nincs hitelem", value: "none" }, ...this.state.banks]}
							prepend={<FontAwesomeIcon icon={fasBuildingColumns} />}
						/>
					</Col>
					<Col lg={4} sm={6}>
						<FormCurrency
							className="admin-only"
							label={
								<>
									<FontAwesomeIcon icon={fasLock} />
									Hitelösszeg
								</>
							}
							name="loanAmount"
							disabled={values.loanBank === "none"}
							validate={this.validLoanAmount}
							autoReset
						/>
					</Col>
				</Row>
			</CollapseBlock>
		);
	}

	private renderMonthlyTransfer(): ReactNode {
		const collapseName = "monthlyTransfer";

		return (
			<CollapseBlock
				title="Havi átutalás"
				name={collapseName}
				active={this.state.collapse[collapseName]}
				onClick={this.onCollapseClick}
			>
				<Row>
					<Col lg={6} md={10} className="pr-lg-5 order-lg-1">
						<CountAmountField
							label={
								<>
									<FontAwesomeIcon icon={fasEarthAmericas} className="mr-2" />
									Netbankos/Mobilbankos utalás
								</>
							}
							info="Átutalási megbízás a bank internetes felületén vagy mobilbankon keresztül, mely néhány bank esetében eltérő díjtételt jelenthet."
							name="internetTransfer"
						/>
					</Col>
					<Col lg={6} md={10} className="pr-lg-5 order-lg-3">
						<CountAmountField
							label={
								<>
									<FontAwesomeIcon icon={fasCommentDollar} className="mr-2" />
									Bankfióki átutalás
								</>
							}
							info="Átutalási megbízás indítása a bank egy fiókjában."
							name="branchTransfer"
						/>
					</Col>
					<Col lg={6} md={10} className="pr-lg-5 order-lg-5">
						<CountAmountField
							label={
								<>
									<FontAwesomeIcon icon={fasPhone} className="mr-2" />
									Telefonos átutalás
								</>
							}
							info="Átutalási megbízás a bank telefonos kapcsolatán keresztül."
							name="phoneTransfer"
						/>
					</Col>
					<Col lg={6} md={10} className="pl-lg-5 order-lg-2">
						<CountAmountField
							label={
								<>
									<FontAwesomeIcon icon={fasInfinity} className="mr-2" />
									Állandó átutalási megbízás
								</>
							}
							name="fixedTransfer"
						/>
					</Col>
					<Col lg={6} md={10} className="pl-lg-5 order-lg-4">
						<CountAmountField
							label={
								<>
									<FontAwesomeIcon icon={fasCommentDollar} className="mr-2" />
									Csoportos beszedés
								</>
							}
							info="A csoportos beszedési megbízás az egyik legkényelmesebb és legolcsóbb módja a havi számlák és csekkek rendszeres befizetésének. A bankszámlánkról a szolgáltató minden hónapban automatikusan beszedi a fizetendő összeget."
							name="directDebit"
						/>
					</Col>
				</Row>
			</CollapseBlock>
		);
	}

	private renderCardAndCash(): ReactNode {
		const collapseName = "cardAndCash";

		return (
			<CollapseBlock
				title="Bankkártyahasználat havonta"
				name={collapseName}
				active={this.state.collapse[collapseName]}
				onClick={this.onCollapseClick}
			>
				<Row>
					<Col lg={6} md={10} className="pr-lg-5 order-lg-1">
						<CountAmountField
							label={
								<>
									<FontAwesomeIcon icon={farMoneyBill1} className="mr-2" />
									Készpénzfelvétel saját ATM-ből
								</>
							}
							name="cashWithdrawalOwnAtm"
						/>
					</Col>
					<Col lg={6} md={10} className="pr-lg-5 order-lg-3">
						<CountAmountField
							label={
								<>
									<FontAwesomeIcon icon={farMoneyBill1} className="mr-2" />
									Készpénzfelvétel idegen ATM-ből
								</>
							}
							name="cashWithdrawalForeignAtm"
						/>
						<FormCheck label="Kérem a 2 ingyenes készpénzfelvételt" name="useFreeCashWithdrawal" />
					</Col>
					<Col lg={6} md={10} className="pl-lg-5 order-lg-2">
						<CountAmountField
							label={
								<>
									<FontAwesomeIcon icon={farCreditCard} className="mr-2" />
									Bankkártyás vásárlás
								</>
							}
							name="cardPurchase"
							maxCount={100}
						/>
					</Col>
				</Row>
			</CollapseBlock>
		);
	}

	private renderNotification(): ReactNode {
		const collapseName = "notification";

		return (
			<CollapseBlock
				title="Értesítések"
				name={collapseName}
				active={this.state.collapse[collapseName]}
				onClick={this.onCollapseClick}
			>
				<Row>
					<Col md={6}>
						<div className="form-label">
							<FontAwesomeIcon icon={fasCommentSms} /> SMS értesítések:
						</div>
						<FormCheck className="mb-1" label="Számlamozgásokról" name="smsServiceTransfers" />
						<FormCheck className="mb-1" label="Kártyahasználatról" name="smsServiceCardUsage" />
						<FormCheck label="Netbank belépésről" name="smsServiceNetbankLogin" />
					</Col>
					<Col md={6}>
						<div className="form-label">
							<FontAwesomeIcon icon={farEnvelope} /> Hagyományos értesítések:
						</div>
						<FormCheck label="Postai úton érkező számlakivonat" name="postalAccountBalance" />
					</Col>
				</Row>
			</CollapseBlock>
		);
	}

	@autobind
	private onCollapseClick(name: CollapseBlocks): void {
		this.setState({
			collapse: {
				...this.state.collapse,
				[name]: !this.state.collapse[name],
			},
		});
	}

	private validReceiptOfMoneyAmount(receiptOfMoneyAmount: number): string {
		if (isNaN(receiptOfMoneyAmount) || receiptOfMoneyAmount < 0) {
			return "Add meg a számlára érkeztetett összeget!";
		}

		if (receiptOfMoneyAmount > MAX_RECEIPT_OF_MONEY_AMOUNT) {
			return `A megadható maximális érték ${formatText(MAX_RECEIPT_OF_MONEY_AMOUNT, "ft")}!`;
		}
	}

	private validPiecesCount(piecesCount: number, values: RetailAccountSearchParams): string {
		const amount = values.receiptOfMoneyAmount;

		if (amount !== 0 && piecesCount < 1) {
			return "A megadott szám nem lehet kevesebb mint 1!";
		}
	}

	private validAge(age: number): string {
		if (!age) {
			return "Add meg az életkorod!";
		}

		if (age < 0) {
			return "Az életkor nem lehet negatív szám!";
		}

		if (age > MAX_AGE) {
			return "Érvénytelen életkor!";
		}
	}

	private validLoanAmount(loanAmount: number): string {
		if (isNaN(loanAmount)) {
			return "Adj meg egy hitelösszeget, vagy válaszd a Hitelező bank alatt a Nincs hitelem opciót!";
		}

		if (loanAmount < 0) {
			return "A hitelösszeg értéke nem lehet negatív szám!";
		}

		if (loanAmount > MAX_LOAN_AMOUNT) {
			return `A megadható maximális hitelösszeg ${formatText(MAX_LOAN_AMOUNT, "ft")}!`;
		}
	}

	@autobind
	private validAverageAccountBalance(averageAccountBalance: number): string {
		if (isNaN(averageAccountBalance)) {
			return "Adj meg átlagos számlaegyenleget!";
		}

		if (averageAccountBalance < 0) {
			return "Az átlagos számlaegyenleg értéke nem lehet negatív szám!";
		}

		if (averageAccountBalance > MAX_AVG_VALUES) {
			return `Az átlagos számlaegyenleg értéke nem haladhatja meg a ${formatText(MAX_AVG_VALUES, "ft")}-ot!`;
		}
	}

	@autobind
	private validAverageSavings(averageSavings: number): string {
		if (averageSavings < 0) {
			return "Az átlagos megtakarítási állomány értéke nem lehet negatív szám!";
		}

		if (averageSavings > MAX_AVG_VALUES) {
			return `Az átlagos megtakarítási állomány értéke nem haladhatja meg a ${formatText(
				MAX_AVG_VALUES,
				"ft",
			)}-ot!`;
		}
	}

	@autobind
	private validCalculationDate(calculationDate: string): string {
		if (!calculationDate) {
			return "Add meg a kalkuláció időpontját!";
		}
	}

	protected getComponentName(): string {
		return "RetailAccountSearchForm";
	}
}
